import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, UserPlus, Pencil, Archive, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/auth";
import { EndClientDialog } from "@/components/intermediary/EndClientDialog";

export default function MyClients() {
  const [clients, setClients] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/me/end-clients?include_archived=${showArchived}`);
    setClients(data.clients || []);
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Retirer ${c.name} de vos clients ?`)) return;
    try {
      const { data } = await api.delete(`/me/end-clients/${c.id}`);
      toast.success(data.archived ? "Client archivé (des commandes y sont rattachées)" : "Client supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const restore = async (c) => {
    try { await api.post(`/me/end-clients/${c.id}/restore`); toast.success("Client restauré"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="cs-cream py-14 min-h-[80vh]" data-testid="page-my-clients">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/espace" className="text-[13px] font-mono text-neutral-500 hover:text-black inline-flex items-center gap-1"><ChevronLeft size={14} /> Retour à mon espace</Link>
        <div className="mt-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="cs-eyebrow text-neutral-500">Espace client</div>
            <h1 className="cs-h1 mt-2 text-4xl">Mes clients</h1>
            <p className="mt-2 text-sm text-neutral-600 max-w-xl">Les clients pour lesquels vous commandez. Ils n'ont pas de compte et n'accèdent ni à votre espace ni à vos commandes.</p>
          </div>
          <button onClick={() => { setEditing(null); setOpen(true); }} className="cs-btn-primary" data-testid="my-clients-add"><UserPlus size={15} /> Ajouter un client</button>
        </div>
        <label className="mt-6 inline-flex items-center gap-2 text-[13px] font-mono text-neutral-600">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="my-clients-show-archived" /> Afficher les clients archivés
        </label>
        <div className="mt-4">
          {clients === null && <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 size={14} className="animate-spin" /> Chargement…</div>}
          {clients?.length === 0 && <div className="cs-card p-8 text-center text-sm text-neutral-600" data-testid="my-clients-empty">Aucun client enregistré pour le moment.</div>}
          {clients?.length > 0 && (
            <div className="cs-card p-0" data-testid="my-clients-list">
              {clients.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-black/10" data-testid={`my-client-${c.id}`}>
                  <div>
                    <div className="font-serif text-lg">{c.name}{c.archived && <span className="ml-2 text-[11px] font-mono text-neutral-500">archivé</span>}</div>
                    <div className="text-[12px] font-mono text-neutral-500">{c.profile_type === "professionnel" ? `SIRET ${c.siret || "—"}` : "Particulier"} · {c.billing_email} · {c.city}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditing(c); setOpen(true); }} className="cs-btn-ghost !text-black !border-black !py-1.5 !px-3 text-[12px]" data-testid={`my-client-edit-${c.id}`}><Pencil size={12} /> <span>Modifier</span></button>
                    {c.archived
                      ? <button onClick={() => restore(c)} className="cs-btn-ghost !text-black !border-black !py-1.5 !px-3 text-[12px]" data-testid={`my-client-restore-${c.id}`}><RotateCcw size={12} /> <span>Restaurer</span></button>
                      : <button onClick={() => remove(c)} className="cs-btn-ghost !text-black !border-black !py-1.5 !px-3 text-[12px]" data-testid={`my-client-remove-${c.id}`}><Archive size={12} /> <span>Retirer</span></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <EndClientDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
      </div>
    </div>
  );
}
